import type { ReactElement } from 'react'
import { sectionAnchors } from '../../../config/routes'

type Feature = {
  icon: ReactElement
  title: string
  description: string
}

// Six app features from the store description, ported verbatim from
// the prototype's .cards markup — rose-underlined titles via the `.u` class.
const FEATURES: Feature[] = [
  {
    icon: (
      <svg viewBox="0 0 24 24" fill="none">
        <path
          d="M12 3l2.1 4.3 4.7.7-3.4 3.3.8 4.7L12 18l-4.2 2.2.8-4.7L5.2 8l4.7-.7L12 3z"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinejoin="round"
        />
      </svg>
    ),
    title: 'AI scores in seconds',
    description: 'Image recognition reads and grades every answer against your key the moment you snap the photo.',
  },
  {
    icon: (
      <svg viewBox="0 0 24 24" fill="none">
        <path d="M4 20v-1a5 5 0 015-5h6a5 5 0 015 5v1" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
        <circle cx="12" cy="8" r="4" stroke="currentColor" strokeWidth="2" />
      </svg>
    ),
    title: 'Names off the page',
    description: "The student's name is pulled straight from the sheet — no roster to build, no manual matching.",
  },
  {
    icon: (
      <svg viewBox="0 0 24 24" fill="none">
        <path
          d="M12 9v4m0 4h.01M10.3 3.9 2.4 18a1.9 1.9 0 001.7 2.9h15.8a1.9 1.9 0 001.7-2.9L13.7 3.9a1.9 1.9 0 00-3.4 0z"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    ),
    title: 'Low-confidence flagged',
    description:
      "Anything the AI wasn't sure about is flagged for a quick manual check — you stay in control of the final score.",
  },
  {
    icon: (
      <svg viewBox="0 0 24 24" fill="none">
        <rect x="3" y="4" width="14" height="16" rx="2" stroke="currentColor" strokeWidth="2" />
        <path d="M7 8h6M7 12h6M7 16h4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
        <path d="M17 8h4v10a2 2 0 01-2 2h-2" stroke="currentColor" strokeWidth="2" />
      </svg>
    ),
    title: 'Multi-page grouped',
    description: 'Submissions that span more than one page are auto-grouped per student, so long exams just work.',
  },
  {
    icon: (
      <svg viewBox="0 0 24 24" fill="none">
        <path
          d="M7 18a5 5 0 01-.5-9.98A6 6 0 0118 9a4 4 0 01-1 7.9"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    ),
    title: 'Saved to the cloud',
    description: 'Every assessment, key, and result is stored securely — pick up on any device, any time.',
  },
  {
    icon: (
      <svg viewBox="0 0 24 24" fill="none">
        <path d="M4 4h16v12H4z" stroke="currentColor" strokeWidth="2" />
        <path d="M8 20h8M12 16v4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
        <path
          d="M8 10l2.5 2.5L16 7"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    ),
    title: 'Export & email',
    description: 'Class results and per-question item analysis export to CSV, emailed straight to your inbox.',
  },
]

export const FeaturesSection = () => {
  return (
    <section id={sectionAnchors.features.slice(1)}>
      <div className="wrap">
        <div className="sec-head">
          <span className="eyebrow">Features</span>
          <h2>Everything you need to stop grading by hand</h2>
          <p>Built for teachers who are tired of marking papers one red pen at a time.</p>
        </div>
        <div className="cards">
          {FEATURES.map((feature) => (
            <div className="card" key={feature.title}>
              <div className="ic">{feature.icon}</div>
              <h3>
                <span className="u">{feature.title}</span>
              </h3>
              <p>{feature.description}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}
